import {
    UseQueryResult,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import { ChartSettingsT, DashboardT, VizType } from 'business/definitions';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import { isChartKey } from '../components/Sentence/helpers/chartKey';
import { useExploreContext } from '../components/Contexts/ExploreContext';
import { useSettingsDispatchContext } from '../components/Contexts/SettingsContext/SettingsContext';
import { ExploreActionType as EAT } from './reducers/exploreReducer';

type ChartTemplateMetadataT = {
    dimensions: SentenceDimensionT[];
    filters: SentenceFilterT[];
    metrics: SentenceMetricT[];
    settings: ChartSettingsT;
    title: string;
    vizType: VizType;
};
export type ChartMetadataT = ChartTemplateMetadataT & {
    dashboard: DashboardT;
    dashboardFilters: DashboardFilterT[];
    disableDashboardFilters: boolean;
    tags: string[];
};
/**
 * Get the chart Metadata
 */
export const useGetChartMetadata = (
    chartKey: number | undefined,
    enabled: boolean = true,
): UseQueryResult<ChartMetadataT, HttpError> =>
    useQuery({
        queryKey: ['chart_metadata', chartKey],
        queryFn: async () => {
            const response = await RQAPICallWithData<ChartMetadataT>({
                endpoint: `explore/chart/${chartKey}/metadata`,
                method: 'GET',
            });
            return response.content.data;
        },
        enabled: isChartKey(chartKey) && enabled,
    });

export const useGetChartTemplateMetadata = (
    chartTemplateKey: number | null,
    isTemplate: boolean,
) =>
    useQuery({
        queryKey: ['chart_template_metadata', chartTemplateKey],
        queryFn: async () => {
            const response = await RQAPICallWithData<ChartTemplateMetadataT>({
                endpoint: `explore/chart_template/${chartTemplateKey}/metadata`,
                method: 'GET',
            });
            return response.content.data;
        },
        enabled: !!chartTemplateKey && isTemplate,
    });

export const useSetChartMetadata = (chartKey: number) => {
    const { dispatchExplore } = useExploreContext();
    const { updateVizSettings } = useSettingsDispatchContext();
    const queryClient = useQueryClient();

    return ({
        title,
        settings,
        vizType,
        tags,
        metrics,
        dimensions,
        filters,
        dashboard,
    }: ChartMetadataT) => {
        // Setting the viz parameters
        const vizSettings = {
            title,
            settings,
            vizType,
            tags,
        };
        updateVizSettings(vizSettings);

        // Set the explore query for future use
        const query = {
            metrics,
            dimensions,
            filters,
        };
        dispatchExplore({ type: EAT.setExplore, query });

        if (!dashboard) {
            console.error(
                `Calling setDashboard with an undefined dashboard within fetchChart for chartKey ${chartKey}`,
            );
        }
        queryClient.setQueryData(
            ['getDashboard', dashboard.slug, ''],
            () => dashboard,
        );
    };
};

export const useSetChartTemplateMetadata = () => {
    const { dispatchExplore } = useExploreContext();
    const { updateVizSettings } = useSettingsDispatchContext();

    return ({
        title,
        settings,
        vizType,
        metrics,
        dimensions,
        filters,
    }: ChartTemplateMetadataT) => {
        const vizSettings = {
            title,
            settings,
            vizType,
        };
        updateVizSettings(vizSettings);

        const query = {
            metrics,
            dimensions,
            filters,
        };
        dispatchExplore({ type: EAT.setExplore, query });
    };
};
